import type { Level, LevelFile } from './engine/level';
import { PlayScreen } from './game/play';
import { isUnlocked, loadProgress, markCleared, saveProgress } from './game/progress';
import { renderSelect } from './game/select';
import { openEditor } from './editor/editor';

const app = document.getElementById('app')!;
let levels: Level[] = [];
let byId = new Map<number, Level>();
let current: { destroy(): void } | null = null;

function route(): { name: 'select' } | { name: 'play'; id: number; draft: boolean } | { name: 'edit'; id: number } {
  const h = location.hash.replace(/^#\/?/, '');
  const [path, q] = h.split('?');
  const m = /^(play|edit)\/(\d+)$/.exec(path ?? '');
  if (!m) return { name: 'select' };
  const id = Number(m[2]);
  if (m[1] === 'edit') return { name: 'edit', id };
  return { name: 'play', id, draft: new URLSearchParams(q).has('draft') };
}

function render() {
  current?.destroy();
  current = null;
  const r = route();
  const debug = new URLSearchParams(location.search).has('debug');
  if (r.name === 'play') {
    let level = byId.get(r.id);
    if (r.draft) {
      try { const d = sessionStorage.getItem('dots-draw.draft'); if (d) level = JSON.parse(d) as Level; } catch { /* ignore */ }
    }
    if (!level) { location.hash = ''; return; }
    if (!r.draft && !isUnlocked(loadProgress(), level.id)) { location.hash = ''; return; } // 잠긴 판
    current = new PlayScreen(app, level, {
      onCleared: (id) => saveProgress(markCleared(loadProgress(), id)),
      onBack: () => { location.hash = ''; },
      onNext: (id) => { location.hash = `play/${id}`; },
      hasNext: (id) => byId.has(id + 1) && isUnlocked(markCleared(loadProgress(), id), id + 1),
    }, { debug });
    const p = loadProgress(); saveProgress({ ...p, last: r.id });
    return;
  }
  if (r.name === 'edit') {
    const level = byId.get(r.id) ?? { id: r.id, obstacles: [{ pts: [[0, 0.9], [1.7692, 0.9], [1.7692, 1], [0, 1]] }], balls: { blue: [0.5, 0.5], pink: [1.5, 0.5], r: 0.031 } };
    current = openEditor(app, level);
    return;
  }
  current = renderSelect(app, levels, loadProgress(), (id) => { location.hash = `play/${id}`; });
}

/** ?debug=1: 기기의 뷰포트 값을 화면에 찍는다(폰에서 레이아웃이 밀릴 때 진단용). */
function installDebug() {
  if (!new URLSearchParams(location.search).has('debug')) return;
  const d = document.createElement('div');
  d.style.cssText = 'position:fixed;left:0;top:0;z-index:99;background:rgba(255,255,0,.85);font:11px monospace;padding:4px;pointer-events:none;white-space:pre';
  document.body.appendChild(d);
  const upd = () => {
    const vv = window.visualViewport;
    const el = document.querySelector<HTMLElement>('.select,.play,.editor');
    const r = el?.getBoundingClientRect();
    d.textContent = `inner ${window.innerWidth}x${window.innerHeight} vv ${vv?.width.toFixed(0)}x${vv?.height.toFixed(0)} off ${vv?.offsetTop.toFixed(0)},${vv?.offsetLeft.toFixed(0)} scale ${vv?.scale.toFixed(2)}\nscreen ${screen.width}x${screen.height} avail ${screen.availWidth}x${screen.availHeight} dpr ${devicePixelRatio}\ndoc ${document.documentElement.clientWidth}x${document.documentElement.clientHeight} scrollY ${scrollY}\nel ${r ? `${r.left.toFixed(0)},${r.top.toFixed(0)} ${r.width.toFixed(0)}x${r.height.toFixed(0)}` : '-'} ${el?.className}\nua ${navigator.userAgent.slice(-60)}`;
  };
  setInterval(upd, 500); upd();
}

/** ?stage=reset: 진행도를 지우고 1판부터. 주소에서 reset 은 떼어 낸다(새로고침 때 또 지워지지 않게). */
function handleReset() {
  const q = new URLSearchParams(location.search);
  if (q.get('stage') !== 'reset') return;
  saveProgress({ cleared: [], last: 1 });
  q.delete('stage');
  const qs = q.toString();
  history.replaceState(null, '', `${location.pathname}${qs ? '?' + qs : ''}#play/1`);
}

async function boot() {
  installDebug();
  handleReset();
  const res = await fetch('/levels.json', { cache: 'no-cache' });
  const file = (await res.json()) as LevelFile;
  levels = file.levels;
  byId = new Map(levels.map((l) => [l.id, l]));
  window.addEventListener('hashchange', render);
  render();
}
boot().catch((e) => { app.textContent = `불러오기 실패: ${e}`; });
